<script lang="ts" setup>
import { computed, ref, type PropType } from 'vue';

interface SetupCriteriaItem {
  timestamp: string | Date
  pumpId: number
  stopPressure: number
  stopTime: number
  stopAmountLitre: number
  stopAmountCemKg: number
  maxFlow: number
}

interface PreStopAmountItem {
  preStopAmountRed: number
  preStopAmountYellow: number
  preStopAmountBlue: number
  preStopAmountGreen: number
}

interface SetupItem {
  timestamp: string | Date
  mode: number
  acceleratorEigenWeight: number
  setupCriteria: SetupCriteriaItem[]
  preStopAmounts: PreStopAmountItem
}

interface SequenceItem {
  recipeId: number
  sequenceNo: number
  materialId: number
  materialName: string
  volume: number
  mixingTime: number
  dryMatter: number
  weight: number
}
interface RecipeItem {
  timestamp: string | Date
  recipeId: number
  recipeName: string
  vcValue: number
  volume: number
  strength: number
  acceleratorEigenWeight: number
  sequence: SequenceItem[]
}

const props = defineProps({
  setupData: {
    type: Array as PropType<SetupItem[]>,
    required: true,
    default: () => []
  },
  recipeData: {
    type: Array as PropType<RecipeItem[]>,
    required: true,
    default: () => []
  },
});

const formatDate = (date: any) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const showLatestOnly = ref(true);

const filteredSetupData = computed(() => {
  if (!props.setupData || props.setupData.length === 0) return [];
  const sortedData = [...props.setupData].reverse();
  if (showLatestOnly.value) {
    const latest = sortedData[0];
    return latest ? [latest] : [];
  }
  return sortedData;
});

const criteriaHeaders = [
  { title: 'Pump', key: 'pumpId', align: 'start' },
  { title: 'Stop Pressure (bar)', key: 'stopPressure', align: 'end' },
  { title: 'Stop Time (s)', key: 'stopTime', align: 'end' },
  { title: 'Stop Amount (L)', key: 'stopAmountLitre', align: 'end' },
  { title: 'Stop Amount (Kg)', key: 'stopAmountCemKg', align: 'end' },
  { title: 'Max Flow (L/min)', key: 'maxFlow', align: 'end' },
] as const;

const sequenceHeaders = [
  { title: 'Seq', key: 'sequenceNo', align: 'start' },
  { title: 'Material', key: 'materialName', align: 'start' },
  { title: 'Volume (L)', key: 'volume', align: 'end' },
  { title: 'Mix Time (s)', key: 'mixingTime', align: 'end' },
  { title: 'Dry Matter (%)', key: 'dryMatter', align: 'end' },
  { title: 'Weight (g/L)', key: 'weight', align: 'end' },
] as const;
</script>

<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Setup History Section -->
    <div class="mb-8">
      <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-4">
        <h3 class="text-h5 font-weight-bold text-primary d-flex align-center">
          <v-icon icon="mdi-history" color="primary" class="mr-2"></v-icon>
          Setup History
        </h3>
        <v-switch
          v-if="setupData && setupData.length > 0"
          v-model="showLatestOnly"
          label="Show latest setup only"
          color="primary"
          hide-details
          density="compact"
          class="ma-0"
        ></v-switch>
      </div>

      <v-alert
        v-if="!setupData || setupData.length === 0"
        type="info"
        variant="tonal"
        text="No Setup data available."
        class="mb-4"
      ></v-alert>

      <template v-else>
        <v-card
          v-for="(setup, index) in filteredSetupData"
          :key="index"
          class="mb-6" elevation="2"
        >
          <v-card-item class="bg-grey-lighten-4 py-3">
            <template v-slot:prepend>
              <v-icon icon="mdi-clock-outline" class="mr-2 text-medium-emphasis"></v-icon>
              <span class="text-subtitle-1 font-weight-medium">{{ formatDate(setup.timestamp) }}</span>
            </template>
            <template v-slot:append>
              <div class="d-flex gap-2 align-center">
                <v-chip color="primary" label size="small" class="font-weight-bold">
                  Mode: {{ setup.mode }}
                </v-chip>
                <v-chip variant="outlined" size="small" class="bg-white">
                  Acc. Weight: {{ setup.acceleratorEigenWeight }}
                </v-chip>
              </div>
            </template>
          </v-card-item>
          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <!-- Pre-Stop Amounts -->
            <div class="mb-6">
              <h4 class="text-subtitle-2 text-uppercase text-medium-emphasis font-weight-bold mb-3">Pre-Stop Amounts</h4>
              <v-row dense>
                <v-col cols="6" sm="3">
                  <v-sheet class="text-center py-3 rounded-lg border" color="red-lighten-5" style="border-color: #fee2e2 !important;">
                    <div class="text-caption text-uppercase font-weight-bold text-red">Red</div>
                    <div class="text-h6 font-weight-bold text-grey-darken-3">{{ setup.preStopAmounts?.preStopAmountRed ?? 0 }}</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-sheet class="text-center py-3 rounded-lg border" color="yellow-lighten-5" style="border-color: #fef9c3 !important;">
                    <div class="text-caption text-uppercase font-weight-bold text-amber-darken-2">Yellow</div>
                    <div class="text-h6 font-weight-bold text-grey-darken-3">{{ setup.preStopAmounts?.preStopAmountYellow ?? 0 }}</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-sheet class="text-center py-3 rounded-lg border" color="blue-lighten-5" style="border-color: #dbeafe !important;">
                    <div class="text-caption text-uppercase font-weight-bold text-blue">Blue</div>
                    <div class="text-h6 font-weight-bold text-grey-darken-3">{{ setup.preStopAmounts?.preStopAmountBlue ?? 0 }}</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-sheet class="text-center py-3 rounded-lg border" color="green-lighten-5" style="border-color: #dcfce7 !important;">
                    <div class="text-caption text-uppercase font-weight-bold text-green">Green</div>
                    <div class="text-h6 font-weight-bold text-grey-darken-3">{{ setup.preStopAmounts?.preStopAmountGreen ?? 0 }}</div>
                  </v-sheet>
                </v-col>
              </v-row>
            </div>

            <!-- Pump Criteria Table -->
            <div>
              <h4 class="text-subtitle-2 text-uppercase text-medium-emphasis font-weight-bold mb-3">Pump Criteria</h4>
              <v-data-table
                :headers="criteriaHeaders"
                :items="setup.setupCriteria"
                density="compact"
                class="border rounded-lg"
                hide-default-footer
              >
                <template v-slot:item.pumpId="{ item }">
                  <v-chip size="x-small" color="info" variant="flat" label>Pump {{ item.pumpId }}</v-chip>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <!-- Recipe Section -->
    <div>
      <h3 class="text-h5 font-weight-bold text-primary mb-4 d-flex align-center">
        <v-icon icon="mdi-flask-outline" color="primary" class="mr-2"></v-icon>
        Recipe & Sequence Information
      </h3>

      <v-alert
        v-if="!recipeData || recipeData.length === 0"
        type="info"
        variant="tonal"
        text="No Recipe data available."
      ></v-alert>

      <template v-else>
        <v-card
          v-for="(recipe, index) in recipeData"
          :key="'recipe-' + index"
          class="mb-6" elevation="2"
          style="border-left: 4px solid #8b5cf6 !important;"
        >
          <v-card-item class="bg-grey-lighten-4 py-3">
            <template v-slot:prepend>
              <v-chip color="grey-darken-3" label size="small" class="font-weight-bold mr-2">
                ID: {{ recipe.recipeId }}
              </v-chip>
              <span class="text-h6 font-weight-bold text-grey-darken-3">{{ recipe.recipeName }}</span>
            </template>
            <template v-slot:append>
              <div class="d-flex align-center gap-2 flex-wrap justify-end">
                <v-chip size="small" variant="outlined" class="bg-white">VC: {{ recipe.vcValue }}</v-chip>
                <v-chip size="small" variant="outlined" class="bg-white">Vol: {{ recipe.volume }}</v-chip>
                <v-chip size="small" variant="outlined" class="bg-white">Str: {{ recipe.strength }}</v-chip>
                <span class="text-caption text-medium-emphasis ml-2 d-none d-sm-inline-block">{{ formatDate(recipe.timestamp) }}</span>
              </div>
            </template>
          </v-card-item>
          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <v-data-table
              :headers="sequenceHeaders"
              :items="recipe.sequence"
              density="compact"
              class="border rounded-lg"
              hide-default-footer
            >
              <template v-slot:item.sequenceNo="{ item }">
                <span class="text-medium-emphasis">#{{ item.sequenceNo }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
.gap-4 {
  gap: 1rem;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>